/**
 * firmaAtamaTalepService.js — Stand Firma Atama Talebi (denetmen tarafı)
 *
 * Base URL: {JAVA_BACKEND_API_URL}/standTalep/firmaAtamaTalep
 * Auth: getAuthHeader() — Basic Auth + X-user-Id + X-token
 *
 * Kaynak: docs/guides/FE-stand-firma-atama-talebi-guide.md
 *
 * Akış özeti: standdaki bir firma, başka bir firmanın (dekoratör ya da taşeron)
 * o standa eklenmesini SERBEST METİNLE ister — istekte hiçbir yerde hedef firmanın
 * id'si yoktur, firma seçici de YOKTUR (bilinçli karar: firma tablosu büyük ve
 * birbirine benzeyen legacy adlarla dolu; yanlış satırı seçmek sessizce yanlış
 * firmayı standa bağlar). Denetmen metni okur, gerçek firmayı bulur ve onaylarken
 * `atananFirmaId`'yi verir. firma_to_stand satırı ancak o anda oluşur.
 *
 * ⚠️ relation_level İSTEKTE DE ONAYDA DA GÖNDERİLMEZ — sunucuda türetilir
 * (katılımcı → 1, level-1 dekoratör → 2, level-2 → 3, taşeron → her zaman 1).
 * Denetmen firmayı doğrular, derinliği değil.
 *
 * ⚠️⚠️ DEĞİŞTİ 2026-09-03 — ONAYIN İKİ HEDEFİ VAR:
 *   A) Bir DEKORATÖRÜN talep ettiği TAŞERON (requestedFirmaTypeId >= 4) artık standa
 *      DEĞİL, dekoratör+fuar'a bağlanıyor: `firma_taseron` satırı + `firma_to_fuar`
 *      yazılıyor, `firma_to_stand` satırı HİÇ OLUŞMUYOR. Yani onay sonrası "firma
 *      standa eklendi" varsayan her ekran bu durumda yanılır.
 *   B) Diğer her şey (katılımcının taşeronu, her dekoratör ataması) eskisi gibi standa.
 *
 * Create yanıtındaki `dekoratoreBaglanacak` bunu ÖNCEDEN söyler — ama `maksimumDerinlikte`
 * gibi SAKLANMAYAN bir alan: yalnızca POST yanıtında gelir, listelerde her satırda
 * `false` okunur. Üstelik bir TAHMİN: çapa onay anında canlı zincirden yeniden
 * hesaplanıyor, talep eden bu arada standdan ayrılırsa yine standa düşebiliyor.
 * ⚠️ Bu yüzden `relationLevel`, `dekoratoreBaglanacak` true iken ANLAMSIZDIR —
 * `firma_taseron`'un derinlik kavramı yok; sayıyı o durumda göstermeyin.
 *
 * ⚠️ Auth: bu beş uç oturumdaki kullanıcıyı OKUR. X-user-Id = 1 (varsayılan bypass
 * kullanıcısı) yerelde MEVCUT DEĞİL → 401 değil 403 alırsınız. Kuyruk ve kararlar
 * için denetmen bir kullanıcı gerekir (yerelde 18).
 */

import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n from "../i18n";

const ATAMA_API = `${JAVA_BACKEND_API_URL}/standTalep/firmaAtamaTalep`;

// ─── MOCK MODU ────────────────────────────────────────────────────────────────
export const USE_MOCK_DATA = true; // Backend hazır olunca false yap

const mockDelay = (ms = 200) => new Promise((r) => setTimeout(r, ms));

// Backend `talep_tarihi`/`islem_tarihi`'ni SQL'den string olarak geçiriyor
// ("2026-08-29 14:02:11.0"); mock da aynı biçimi üretsin ki panel iki modda aynı
// görünsün (tarih olduğu gibi basılıyor, parse edilmiyor).
const mockTimestamp = () => new Date().toISOString().replace('T', ' ').replace(/\.\d+Z$/, '.0');

// ─── Sabitler ─────────────────────────────────────────────────────────────────
// is_processed ÜÇ DURUMLU. Doğruluk kontrolü (`if (isProcessed)`) reddedilmiş bir
// talebi "hâlâ bekliyor" gibi gösterir — bkz. FE-README-guide.md 2026-08-29 notu.
export const ATAMA_TALEP_DURUM = {
  0: { get label() { return i18n.t("services:firmaAtamaTalepService.bekliyor"); }, color: 'warning' },
  1: { get label() { return i18n.t("services:firmaAtamaTalepService.islendi"); }, color: 'success' },
  2: { get label() { return i18n.t("services:firmaAtamaTalepService.reddedildi"); }, color: 'danger' },
};

export const RED_NEDENI_MAX = 500;

// SADECE MOCK için rol etiketleri. Canlı yanıt `requestedFirmaTypeAdi`'yi zaten
// COALESCE(firma_type_text, firma_type_value) ile dolduruyor (14 Güvenlik dahil);
// bu harita yalnızca mock modda yeni açılan talebin "Tip 7" gibi görünmemesi için.
const MOCK_FIRMA_TYPE_LABELS = {
  3: 'Dekoratör', 4: 'Taşeron', 5: 'Nakliye', 6: 'Zemin', 7: 'Temizlik', 8: 'TEM',
  9: 'Katılımcı Destek Personeli', 10: 'OPERASYON', 11: 'PROJE DESTEK',
  12: 'OPERASYON DESTEK', 13: 'ISG DESTEK', 14: 'Güvenlik',
};

// relation_level — sunucuda türetilir, sadece GÖSTERİLİR.
export const RELATION_LEVEL_LABELS = {
  get 1() { return i18n.t("services:firmaAtamaTalepService.anaFirmaSeviye1"); },
  get 2() { return i18n.t("services:firmaAtamaTalepService.altYukleniciSeviye2"); },
  get 3() { return i18n.t("services:firmaAtamaTalepService.altYukleniciSeviye3"); },
};

let _mockTalepler = [
  {
    id: 6283,
    standId: 323,
    standName: 'A-201',
    fuarId: null,
    talepEdenFirmaId: 73,
    talepEdenFirmaAdi: 'ÇARŞIBAŞI KOZMETİK TEKSTİL LTD. ŞTİ.',
    talepEdenUserId: 18,
    requestedFirmaTypeId: 3,
    requestedFirmaTypeAdi: 'Dekoratör',
    firmaNameText: 'MONSTAR TASARIM',
    firmaContactNameText: 'Barış Gülsevil',
    firmaContactPhoneText: '0532 000 00 00',
    firmaContactEmailText: 'info@monstar.example',
    relationLevel: 1,
    maksimumDerinlikte: false,
    dekoratoreBaglanacak: false,
    belgePenceresiKapali: false,
    isProcessed: 0,
    durum: 'bekliyor',
    atananFirmaId: null,
    atananFirmaAdi: null,
    islemYapanUserId: null,
    redNedeni: null,
    talepTarihi: '2026-08-29 14:02:11.0',
    islemTarihi: null,
  },
  {
    id: 6284,
    standId: 323,
    standName: 'A-201',
    fuarId: null,
    talepEdenFirmaId: 500,
    talepEdenFirmaAdi: 'Test Decorator',
    talepEdenUserId: 18,
    requestedFirmaTypeId: 14,
    requestedFirmaTypeAdi: 'Güvenlik',
    firmaNameText: 'ANADOLU GÜVENLİK HİZMETLERİ',
    firmaContactNameText: 'Selin Aydın',
    firmaContactPhoneText: '0533 111 11 11',
    firmaContactEmailText: null,
    relationLevel: 1,
    maksimumDerinlikte: false,
    dekoratoreBaglanacak: false,
    belgePenceresiKapali: true,
    isProcessed: 0,
    durum: 'bekliyor',
    atananFirmaId: null,
    atananFirmaAdi: null,
    islemYapanUserId: null,
    redNedeni: null,
    talepTarihi: '2026-08-28 09:41:03.0',
    islemTarihi: null,
  },
];

// Serbest metin alanlarının tamamı VARCHAR(255); 255'i aşan değer 400 döner
// (sessiz kırpma YOK), bu yüzden sınır formda.
export const TALEP_TEXT_MAX = 255;

// Hata gövdeleri tutarsız (text/plain, GenericResultJson ya da boş) — durum koduna
// bakıp Türkçe mesajı olabildiğince gövdeden çıkarıyoruz. 409 bu ailenin ayırt
// edici kodu: mükerrer talep, çoktan işlenmiş talep, ya da standda zaten başka bir
// seviye-1 dekoratör var demek.
function atamaError(error, fallback) {
  const status = error?.response?.status;
  const body = error?.response?.data;
  const message =
    (typeof body === 'string' && body) ||
    body?.message ||
    fallback;
  const err = new Error(message);
  err.status = status;
  return err;
}

/**
 * Talep AÇ — standdaki bir firma, başka bir firmanın standa eklenmesini ister.
 * POST /standTalep/firmaAtamaTalep
 *
 * ⚠️ Gövdede hedef firmanın ID'si YOK ve OLMAYACAK: kullanıcı adı SERBEST METİN
 * yazar, denetmen okuyup gerçek firmayı seçer. Bu ekrana `/firma/list` üzerinden
 * bir ara-seç kutusu KOYMAYIN (bkz. rehber bölüm 1, Deniz'in 2026-08-29 kararı).
 * ⚠️ `relationLevel` de yok — sunucu türetir; cevapta gelen değer bilgilendirmedir.
 * ⚠️ `firmaId` TALEBİ AÇAN firmadır ve oturumdaki kullanıcı o firmaya bağlı
 * olmalıdır (`user_to_firma`), yoksa 403. Denetmen olmak muafiyet sağlamaz.
 * ⚠️ requestedFirmaTypeId 3 (Dekoratör) ya da >= 4 olmalı; 1/2 gönderilirse 400.
 * ⚠️ 409 = aynı firma, aynı stand, aynı rol ve aynı metinle bekleyen bir talep var.
 */
export async function createFirmaAtamaTalebi(
  {
    standId, fuarId, firmaId, requestedFirmaTypeId,
    firmaNameText, firmaContactNameText = null, firmaContactPhoneText = null, firmaContactEmailText = null,
  },
  { forceLive = false } = {}
) {
  // Backend her metni trim'liyor ve boş string'i null'a çeviriyor — aynısını burada
  // yapıp gönderiyoruz ki mock ve canlı aynı kaydı üretsin.
  const clean = (v) => {
    const s = String(v ?? '').trim();
    return s === '' ? null : s;
  };
  const body = {
    standId: Number(standId),
    fuarId: Number(fuarId),
    firmaId: Number(firmaId),
    requestedFirmaTypeId: Number(requestedFirmaTypeId),
    firmaNameText: clean(firmaNameText),
    firmaContactNameText: clean(firmaContactNameText),
    firmaContactPhoneText: clean(firmaContactPhoneText),
    firmaContactEmailText: clean(firmaContactEmailText),
  };

  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const created = {
      id: Math.max(0, ..._mockTalepler.map((t) => t.id)) + 1,
      ...body,
      standName: `Stand #${body.standId}`,
      talepEdenFirmaId: body.firmaId,
      talepEdenFirmaAdi: `Firma #${body.firmaId}`,
      talepEdenUserId: null,
      requestedFirmaTypeAdi:
        MOCK_FIRMA_TYPE_LABELS[body.requestedFirmaTypeId] || `Tip ${body.requestedFirmaTypeId}`,
      relationLevel: 1,
      maksimumDerinlikte: false,
      // Mock tarafında kaba bir tahmin: talep edilen rol TAŞERON (>=4) ise dekoratöre
      // bağlanacağını varsayıyoruz. Canlıda backend bunu standdaki gerçek zincirden
      // hesaplıyor (talep eden bu standın dekoratörü mü, katılımcısı değil mi).
      dekoratoreBaglanacak: body.requestedFirmaTypeId >= 4,
      belgePenceresiKapali: false,
      isProcessed: 0,
      durum: 'bekliyor',
      atananFirmaId: null,
      atananFirmaAdi: null,
      islemYapanUserId: null,
      redNedeni: null,
      talepTarihi: mockTimestamp(),
      islemTarihi: null,
    };
    _mockTalepler = [created, ..._mockTalepler];
    return { ...created };
  }

  try {
    const res = await axios.post(ATAMA_API, body, { headers: getAuthHeader() });
    return res.data;
  } catch (error) {
    console.error('Error creating firma atama talebi:', error);
    throw atamaError(error, i18n.t("services:firmaAtamaTalepService.talepOlusturulamadi"));
  }
}

/**
 * Talep eden firmanın KENDİ listesi.
 * GET /standTalep/firmaAtamaTalep/firma?firmaId=&fuarId=&standId=
 *
 * `firmaId` ve `fuarId` ZORUNLU (eksikse Spring handler'a girmeden 400 verir).
 * `standId` opsiyonel daraltma. Bu firmanın legacy satırları da (standId null)
 * listeye dahil olur — onlar bu uçlardan işlenemez, eski ekranda kalırlar.
 */
export async function getKendiAtamaTalepleri(
  { firmaId, fuarId, standId = null } = {},
  { forceLive = false } = {}
) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    // fuarId filtresi yok — yukarıdaki getAtamaTalepleri ile aynı sebep.
    return _mockTalepler
      .filter((t) => String(t.talepEdenFirmaId) === String(firmaId))
      .filter((t) => (standId == null || String(t.standId) === String(standId)))
      .sort((a, b) => b.id - a.id)
      .map((t) => ({ ...t }));
  }

  try {
    const res = await axios.get(`${ATAMA_API}/firma`, {
      params: { firmaId, fuarId, standId: standId ?? undefined },
      headers: getAuthHeader(),
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error('Error fetching own firma atama talepleri:', error);
    throw atamaError(error, i18n.t("services:firmaAtamaTalepService.taleplerinizAlinamadi"));
  }
}

/**
 * Denetmen kuyruğu — bir fuarın atama talepleri.
 * GET /standTalep/firmaAtamaTalep/bekleyen?fuarId=&standId=&durum=&legacyDahil=
 *
 * Sıralama: is_processed ASC, id DESC → önce bekleyenler, her grupta en yeni üstte.
 * SAYFASIZ düz dizi döner (zarf yok, totalCount yok) — uzarsa istemcide sayfalayın.
 * Legacy satırlar (standId null) `legacyDahil=true` olmadan gelmez; gelseler bile
 * bu uçlardan ONAYLANAMAZLAR.
 */
export async function getAtamaTalepleri(
  { fuarId, standId = null, durum = null, legacyDahil = false } = {},
  { forceLive = false } = {}
) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    // fuarId'ye göre FİLTRELENMİYOR — standOverviewService.getStandOverviewList
    // mock modundaki aynı karar (bkz. oradaki _mockFuarId notu): mock fuarId
    // localStorage'dan geliyor, sabit bir değere eşitlemek fixture'ı erişilemez
    // kılar. standId zaten tekil.
    return _mockTalepler
      .filter((t) => (standId == null || String(t.standId) === String(standId)))
      .filter((t) => (durum == null || durum === '' || Number(t.isProcessed) === Number(durum)))
      .filter((t) => (legacyDahil ? true : t.standId != null))
      .sort((a, b) => a.isProcessed - b.isProcessed || b.id - a.id)
      .map((t) => ({ ...t }));
  }

  try {
    const res = await axios.get(`${ATAMA_API}/bekleyen`, {
      params: {
        fuarId,
        standId: standId ?? undefined,
        durum: durum === null || durum === '' ? undefined : durum,
        legacyDahil: legacyDahil || undefined,
      },
      headers: getAuthHeader(),
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch (error) {
    console.error('Error fetching firma atama talepleri:', error);
    throw atamaError(error, i18n.t("services:firmaAtamaTalepService.atamaTalepleriAlinamadi"));
  }
}

/**
 * Talebi ONAYLA — serbest metnin işaret ettiği gerçek firmayı bildirir.
 * POST /standTalep/firmaAtamaTalep/{talepId}/ata   body: { atananFirmaId }
 *
 * Gövdede relationLevel YOK: seviye onay anındaki zincirden yeniden hesaplanır.
 * ⚠️ 409 = standda zaten BAŞKA bir seviye-1 dekoratör var (mesaj mevcut firmayı
 * adıyla söyler), ya da talep bu arada işlenmiş. Denetmen önce eskisini
 * DELETE /standTalep/deleteFirmaFromStand/{relationId} ile çözmeli.
 * Firma zaten o rolde standa bağlıysa talep yine kapanır ama İKİNCİ SATIR YAZILMAZ.
 */
export async function ataAtamaTalebi(talepId, atananFirmaId, { forceLive = false } = {}) {
  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const talep = _mockTalepler.find((t) => String(t.id) === String(talepId));
    if (!talep) throw new Error('Talep bulunamadı.');
    if (talep.isProcessed !== 0) {
      const err = new Error('Bu talep daha önce işlenmiş.');
      err.status = 409;
      throw err;
    }
    talep.isProcessed = 1;
    talep.durum = 'islendi';
    talep.atananFirmaId = Number(atananFirmaId);
    // Mock katmanı firma ADLARINI bilmiyor (firma araması ayrı bir serviste) —
    // uydurma bir "Firma #47431" dizesi basmak yerine null bırakıyoruz; paneldeki
    // fallback az önce SEÇİLEN firmanın adını yazıyor. Canlı yanıt bu alanı gerçek
    // adla dolduruyor, dolayısıyla iki modda da doğru ad görünüyor.
    talep.atananFirmaAdi = null;
    talep.islemTarihi = mockTimestamp();
    return { ...talep };
  }

  try {
    const res = await axios.post(
      `${ATAMA_API}/${talepId}/ata`,
      { atananFirmaId: Number(atananFirmaId) },
      { headers: getAuthHeader() }
    );
    return res.data;
  } catch (error) {
    console.error('Error approving firma atama talebi:', error);
    throw atamaError(error, i18n.t("services:firmaAtamaTalepService.talepOnaylanamadi"));
  }
}

/**
 * Talebi REDDET — gerekçe zorunlu, en fazla 500 karakter, talep eden firma görür.
 * POST /standTalep/firmaAtamaTalep/{talepId}/reddet   body: { redNedeni }
 */
export async function reddetAtamaTalebi(talepId, redNedeni, { forceLive = false } = {}) {
  const reason = String(redNedeni || '').trim();

  if (USE_MOCK_DATA && !forceLive) {
    await mockDelay();
    const talep = _mockTalepler.find((t) => String(t.id) === String(talepId));
    if (!talep) throw new Error('Talep bulunamadı.');
    if (talep.isProcessed !== 0) {
      const err = new Error('Bu talep daha önce işlenmiş.');
      err.status = 409;
      throw err;
    }
    talep.isProcessed = 2;
    talep.durum = 'reddedildi';
    talep.redNedeni = reason;
    talep.islemTarihi = mockTimestamp();
    return { ...talep };
  }

  try {
    const res = await axios.post(
      `${ATAMA_API}/${talepId}/reddet`,
      { redNedeni: reason },
      { headers: getAuthHeader() }
    );
    return res.data;
  } catch (error) {
    console.error('Error rejecting firma atama talebi:', error);
    throw atamaError(error, 'Talep reddedilemedi.');
  }
}
